import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import { Link, useParams } from 'react-router-dom'
import { Button, Modal } from '@mui/material'
import { toast } from 'react-toastify'
import { setPage } from '../../store/slice/page'

import PageWrapper from '../../containers/PageWrapper'

import { ReactComponent as CloseIcon } from '../../assets/icons/cancel.svg'
import { ReactComponent as CopyPasteIcon } from '../../assets/icons/copy-paste.svg'
import { channelsList, getChannelsAction } from '../../store/slice/channel'
import { userData } from '../../store/slice/user'
import ProductsTable from './Table'
import CustomCheckbox from '../../components/CustomCheckbox'
import CustomTextarea from '../../components/CustomTextarea'
import SearchBar from '../../components/Form/SearchBar'

import { TOAST_DEFAULT_CONFIG } from '../../constants/toast'
import { addProductsEan, getProductsCanBeAdded } from '../../api/product'
import { copyToBuffer } from '../../utils/copy-to-buffer'

import useStyles from './styles'

const AddProduct = () => {
  const user = useSelector(userData)
  const classes = useStyles()
  const dispatch = useDispatch()
  const channelData = useParams()
  const channelsDataList = useSelector(channelsList)
  const [productsList, setProductList] = useState([])
  const [textData, setDataText] = useState('')
  const [search, setSearch] = useState('')
  const [notFoundElems, setNotFoundElems] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [tableList, setTableList] = useState([])
  const [isLoadingTable, setIsLoadingTable] = useState(true)
  const [isAddingProducts, setIsAddingProducts] = useState(false)
  const [addAsActive, setAddAsActive] = useState(true)

  const selectAll = useMemo(() => tableList.every((item) => item.selected), [tableList])
  const isSomeSelected = useMemo(() => tableList.some((item) => item.selected), [tableList])
  const selectedCount = useMemo(() => tableList.filter((item) => item.selected).length, [tableList])
  const currentChannelData = useMemo(
    () => channelsDataList.find((el) => el.id.toString() === channelData.id.toString()),
    [channelsDataList, channelData]
  )

  const onSearch = (str) => {
    setSearch(str)
    if (!str) {
      setTableList(productsList)
      return
    }
    const arr = productsList?.filter(
      (item) =>
        item.productLabel.toLowerCase().includes(str.toLowerCase()) ||
        item.EAN.toLowerCase().includes(str.toLowerCase()) ||
        item.productBrand.toLowerCase().includes(str.toLowerCase())
    )
    setTableList(arr)
  }

  const toggleSelectAll = () => {
    const mappedList = tableList.map((item) => ({ ...item, selected: !selectAll }))

    setProductList(mappedList)
    setTableList(mappedList)
  }

  const onChangeSelect = (id, value) => {
    const mappedList = tableList.map((item) => {
      if (id === item.id) {
        return {
          ...item,
          selected: value,
        }
      }
      return item
    })
    setProductList(mappedList)
    setTableList(mappedList)
  }

  const onSelectItems = () => {
    const arr = textData.split('\n')
    const notFound = []
    const mappedList = tableList.map((item) => {
      const res = arr.find((el) => el === item.EAN)
      if (res) {
        return {
          ...item,
          selected: true,
        }
      }

      return item
    })

    arr.forEach((item) => {
      const elem = tableList.find((ean) => ean.EAN === item)

      if (!elem) {
        notFound.push(item)
      }
    })

    if (notFound.length) {
      setIsModalOpen(true)
    }

    setNotFoundElems(notFound)
    setProductList(mappedList)
    setTableList(mappedList)
  }

  const fetchTableData = (accountID, channelID) => {
    setIsLoadingTable(true)
    getProductsCanBeAdded(accountID, channelID)
      .then((res) => {
        if (res.status === 200) {
          const data = res.data.map((item, index) => ({
            ...item,
            id: index,
            selected: false,
          }))
          setProductList(data)
          setTableList(data)
        }
      })
      .finally(() => {
        setIsLoadingTable(false)
      })
      .catch((err) => {
        toast.error(err.response.data?.Error, TOAST_DEFAULT_CONFIG)
      })
  }

  const onAddProduct = () => {
    setIsAddingProducts(true)
    const formDataList = tableList
      .filter((el) => el.selected)
      .map((el) => {
        if (el.ASINandSKU && (el.ASINandSKU.ASIN || el.ASINandSKU.SKU)) {
          return { ASINandSKU: el.ASINandSKU }
        }
        if (el.EAN) {
          return { EAN: el.EAN }
        }
        return { EAN: '' }
      })

    addProductsEan(user?.accountID, channelData.id, formDataList, addAsActive)
      .then((res) => {
        if (res.status === 200) {
          // const arrMess = res.data.informationMessages.map((item) => item.informationMessage)
          fetchTableData(user?.accountID, channelData.id)
          // toast.success(arrMess.join('\n'), TOAST_DEFAULT_CONFIG)
          toast.success(
            'Advertising campaigns for the selected product(s) are being generated. They are visible now in the cockpit and will be available for advertising after 24hrs at the latest.',
            TOAST_DEFAULT_CONFIG
          )
          setSearch('')
          setDataText('')
        }
      })
      .finally(() => {
        setIsAddingProducts(false)
      })
      .catch((err) => {
        let arrMess = []
        if (err.response.data.errors) {
          arrMess = err.response.data.errors.map((item) => item.message)
        } else if (err.response.data.errorMessages) {
          arrMess = err.response.data.errorMessages.map((item) => item.errorMessage)
        } else if (err.response.data.Error) {
          arrMess = [err.response.data.Error]
        }
        toast.error(arrMess.join(' '), TOAST_DEFAULT_CONFIG)
      })
  }

  const onModalClose = () => {
    setIsModalOpen(false)
    setNotFoundElems([])
  }

  const copyHandler = () => {
    copyToBuffer(notFoundElems.join('\n'), () => {
      toast.success('Copied to clipboard')
      onModalClose()
    })
  }

  useEffect(() => {
    dispatch(setPage('addChannel'))
  })

  useEffect(() => {
    if (user?.accountID && channelData.id) {
      fetchTableData(user?.accountID, channelData.id)
    }
  }, [user?.accountID, channelData.id])

  useEffect(() => {
    if (user?.accountID) {
      dispatch(getChannelsAction(user?.accountID))
    }
  }, [user])

  return (
    <PageWrapper>
      <Box className={classes.page_wrapper}>
        <Box className={classes.page_content}>
          <div className={classes.header_title}>Add new products to {currentChannelData?.channelName}</div>
          <Box className={classes.description}>
            <div className={classes.descriptionText}>
              Only products that are not currently being advertised are shown below. To advertise new products, select
              {/* eslint-disable-next-line react/no-unescaped-entities */}
              one or more items and click "Add products."
              <br />
              <br />
              Important: Use the DeepCliq interface to start or pause ads for your products. Changes made in the Bol
              interface will be overwritten overnight. Do not manually archive DeepCliq campaigns in Bol (if you need to
              archive a campaign, please contact: <a href="mailto:support@deepcliq.ai">support@deepcliq.ai</a>).
            </div>
            <Box className={classes.wrapperFlex}>
              <Box className={classes.rightBlock}>
                <Box className={classes.wrapperTable}>
                  <ProductsTable
                    search={search}
                    onChangeSelect={onChangeSelect}
                    list={tableList}
                    accountId={user?.accountID}
                    loading={isLoadingTable || isAddingProducts}
                    loadingText={isAddingProducts ? 'Please wait, adding product...' : 'Please wait, loading data...'}
                  />
                </Box>
                <Box className={classes.wrapperBottom}>
                  <Box className={classes.flexWrapper}>
                    <CustomCheckbox
                      disabled={isLoadingTable || isAddingProducts || !tableList.length}
                      value={selectAll}
                      onChange={() => toggleSelectAll()}
                    />
                    <p className={classes.labelText}>Select all</p>
                  </Box>
                  <Box className={classes.flexWrapper}>
                    <CustomCheckbox
                      disabled={isLoadingTable || isAddingProducts || !tableList.length}
                      value={addAsActive}
                      onChange={setAddAsActive}
                    />
                    <p className={classes.labelText}>Add as active (Ads start tomorrow)</p>
                  </Box>
                  <Box className={classes.addProducts}>
                    <Button
                      disabled={!isSomeSelected || isAddingProducts}
                      onClick={onAddProduct}
                      variant="outline"
                      size="large"
                      color="primary"
                      className={classes.form_btn}
                      type="submit"
                    >
                      Add products
                    </Button>
                    <Box className={classes.selectedCount}>{selectedCount} selected</Box>
                  </Box>
                </Box>
              </Box>
              <Box className={classes.leftBlock}>
                <SearchBar
                  value={search}
                  onSubmit={onSearch}
                  onChange={(str) => {
                    if (!str) {
                      onSearch('')
                    }
                    setSearch(str)
                  }}
                  isClearButtonShowAfterSearch
                  onClear={onSearch}
                  placeholder="Search EAN, Name or Brand...."
                />
                <Box className={classes.wrapperArea}>
                  <CustomTextarea
                    customClass={classes.textArea}
                    placeholder="Copy and paste the EANs you want to select in the list into this text box..."
                    label="Bulk EAN selection (copy&paste, one EAN per line)"
                    value={textData}
                    onChange={(val) => {
                      setDataText(val)
                    }}
                  />
                  <Box className={classes.wrapperSelectBtn}>
                    <Box className={classes.form_footer_box}>
                      <Button
                        disabled={!textData?.length || !tableList.length}
                        onClick={onSelectItems}
                        variant="outline"
                        size="large"
                        color="primary"
                        className={classes.form_btn}
                        type="submit"
                      >
                        Select
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Link className={classes.closeBtn} to="/products">
          <CloseIcon />
        </Link>
      </Box>
      <Modal
        open={isModalOpen}
        onClose={onModalClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
        className={classes.modal}
      >
        <Box className={classes.wrapper}>
          <Box className={classes.wrapper_header}>
            <div className={classes.modal_title}>These products are currently not available for advertising:</div>
            <button className={[classes.closeBtn, classes.close_button].join(' ')} onClick={onModalClose} type="button">
              <CloseIcon />
            </button>
          </Box>
          <Box className={classes.wrapper_body}>
            {notFoundElems.map((item) => (
              <div>{item}</div>
            ))}
          </Box>
          <Box className={classes.wrapper_footer}>
            {notFoundElems.length} product{notFoundElems.length === 1 ? '' : 's'}
            <Button variant="outlined" color="primary" onClick={copyHandler} className={classes.footer_btn}>
              <CopyPasteIcon /> Copy all
            </Button>
          </Box>
        </Box>
      </Modal>
    </PageWrapper>
  )
}
export default AddProduct
